import { getPlanLabel } from '../../lib/utils'
import { useLocale } from '../../i18n/LocaleProvider'

export function Badge({ children, variant = 'gray', size = 'md', dot = false }) {
  const variants = {
    gray: 'badge-gray',
    blue: 'badge-blue',
    green: 'badge-green',
    yellow: 'badge-yellow',
    red: 'badge-red',
    purple: 'badge-purple',
    orange: 'inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700',
  }

  const sizes = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-xs px-2.5 py-0.5',
    lg: 'text-sm px-3 py-1',
  }

  const dotColors = {
    gray: 'bg-gray-400',
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    yellow: 'bg-yellow-500',
    red: 'bg-red-500',
    purple: 'bg-purple-500',
    orange: 'bg-orange-500',
  }

  return (
    <span className={`${variants[variant]} ${sizes[size]}`}>
      {dot && (
        <span
          className={`w-1.5 h-1.5 rounded-full ${dotColors[variant]}`}
        />
      )}
      {children}
    </span>
  )
}

export function PlanBadge({ plan, size = 'md' }) {
  const colorMap = {
    free: 'gray',
    starter: 'blue',
    pro: 'green',
    business: 'purple',
    enterprise: 'yellow',
    admin: 'purple',
  }

  return (
    <Badge variant={colorMap[plan] || 'gray'} size={size}>
      {getPlanLabel(plan)}
    </Badge>
  )
}

export function ScoreBadge({ score, size = 'md' }) {
  const { t } = useLocale()
  const getVariant = (score) => {
    if (score >= 90) return 'green'
    if (score >= 70) return 'blue'
    if (score >= 50) return 'yellow'
    return 'red'
  }

  return (
    <Badge variant={getVariant(score)} size={size}>
      {getLocalizedScoreLabel(score, t)} - {score}
    </Badge>
  )
}

export function StatusBadge({ status, size = 'md' }) {
  const { t } = useLocale()
  const config = {
    active: { variant: 'green', label: t('authenticated.badges.status.active'), dot: true },
    archived: { variant: 'gray', label: t('authenticated.badges.status.archived'), dot: true },
    closed: { variant: 'red', label: t('authenticated.badges.status.closed'), dot: true },
    pending: { variant: 'yellow', label: t('authenticated.badges.status.pending'), dot: true },
    shortlisted: { variant: 'green', label: t('authenticated.badges.status.shortlisted'), dot: true },
    rejected: { variant: 'red', label: t('authenticated.badges.status.rejected'), dot: true },
    processing: { variant: 'blue', label: t('authenticated.badges.status.processing'), dot: true },
  }

  const { variant, label, dot } = config[status] || {
    variant: 'gray',
    label: status,
    dot: false,
  }

  return (
    <Badge variant={variant} size={size} dot={dot}>
      {label}
    </Badge>
  )
}

export function RoleBadge({ role, size = 'md' }) {
  const { t } = useLocale()
  const config = {
    owner: { variant: 'purple', label: t('authenticated.badges.role.owner') },
    admin: { variant: 'blue', label: t('authenticated.badges.role.admin') },
    member: { variant: 'gray', label: t('authenticated.badges.role.member') },
  }

  const { variant, label } = config[role] || { variant: 'gray', label: role }

  return (
    <Badge variant={variant} size={size}>
      {label}
    </Badge>
  )
}

function getLocalizedScoreLabel(score, t) {
  if (score >= 85) return t('authenticated.badges.score.excellent')
  if (score >= 70) return t('authenticated.badges.score.good')
  if (score >= 50) return t('authenticated.badges.score.partial')
  return t('authenticated.badges.score.poor')
}
